"use client";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { getCookie } from "cookies-next";
import { useTranslations } from "next-intl";
import { message } from "antd";
import { useUserStore } from "@/store/user.store";
import { useUserAuth } from "@/lib/hooks/useUserAuth";
import { useRouter } from "@/navigation";

type FormValues = {
	email?: string;
	password?: string;
	remember?: boolean;
	onSubmit: (data?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
	setLoginModalOn?: any;
};

interface LoginFormProps {
	setLoginModalOn?: any;
}

const LoginForm = ({ setLoginModalOn }: LoginFormProps) => {
	const t = useTranslations();
	const { setUserLoginInfo } = useUserStore();
	let get_form_info: any = getCookie("created__user__info");
	if (get_form_info) {
		get_form_info = JSON.parse(get_form_info);
	}
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<FormValues>();

	const [loading, setLoading] = React.useState(false);
	const { useLogin } = useUserAuth();
	const router = useRouter();
	const onSubmit: SubmitHandler<FormValues> = async (data: any) => {
		setLoading(true);

		try {
			setLoading(true);
			await useLogin(data);
			setLoginModalOn(false);
		} catch (e: any) {
			message.error(e?.message || "error");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="space-y-5">
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
				<div>
					<label
						htmlFor="login-email"
						className="mb-1.5 block text-left text-[13px] font-medium text-gray-700"
					>
						{t("common.Email")}
					</label>
					<input
						type="text"
						id="login-email"
						className={`w-full rounded-md border bg-gray-50/60 px-4 py-2.5 text-sm text-gray-900 outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-[#c07d3e] focus:bg-white focus:ring-1 focus:ring-[#c07d3e]/20 ${
							errors.email ? "border-red-400 bg-red-50/40" : "border-gray-200"
						}`}
						defaultValue={get_form_info?.email}
						placeholder={t("form.109a2790cc6b48461908f883b7b41549f9b3")}
						{...register("email", { required: true })}
					/>
					{errors.email && <p className="mt-1 text-xs text-red-500">{t("common.Email")} is required</p>}
				</div>

				<div>
					<label
						htmlFor="login-password"
						className="mb-1.5 block text-left text-[13px] font-medium text-gray-700"
					>
						{t("common.Password")}
					</label>
					<input
						type="password"
						id="login-password"
						className={`w-full rounded-md border bg-gray-50/60 px-4 py-2.5 text-sm text-gray-900 outline-none transition-colors duration-200 placeholder:text-gray-400 focus:border-[#c07d3e] focus:bg-white focus:ring-1 focus:ring-[#c07d3e]/20 ${
							errors.password ? "border-red-400 bg-red-50/40" : "border-gray-200"
						}`}
						placeholder="••••••••"
						{...register("password", {
							required: true,
						})}
					/>
					{errors.password && <p className="mt-1 text-xs text-red-500">{t("common.Password")} is required</p>}
				</div>

				<button
					type="submit"
					disabled={loading}
					className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-md bg-[#ff9f0a] px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-[#e68d00] focus:outline-none focus:ring-2 focus:ring-[#ff9f0a]/25 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{loading && (
						<span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
					)}
					{loading ? t("message.7f9e518a7a1d1e4bc3e8990bed1d9be4d404") + "..." : t("common.Sign_in")}
				</button>
			</form>
		</div>
	);
};

export default LoginForm;
